import { useEffect, useState } from "react";
import { getDynamicDropdownByCategory } from "../../views/Admin/DynamicDropdowns/manageDynamicDropdowns";
import { DropdownOptions } from "../FormFields/DropdownField";
import Loading from "../Loading/Loading";
import MultiDropdown, { MultiDropdownProps } from "./MultiDropdown";

interface DynamicMultiDropdownFieldProps
	extends Omit<MultiDropdownProps, "options" | "selectedValues" | "onChange"> {
	category: string;
	/**
	 * Saved values from parent:
	 * - default: number[] (ids)
	 * - if saveLabelAsValue: string[] (labels)
	 */
	value?: Array<number | string>;

	/**
	 * Callback to parent:
	 * - default: number[] (ids)
	 * - if saveLabelAsValue: string[] (labels)
	 */
	onChange: (values: Array<number | string>) => void;

	/**
	 * When true, option.value is the label, so the saved values are labels.
	 * Default: false (option.value is id, saved values are ids)
	 */
	saveLabelAsValue?: boolean;
}

const DynamicMultiDropdownField = ({
	category,
	value = [],
	onChange,
	saveLabelAsValue = false,
	...rest
}: DynamicMultiDropdownFieldProps) => {
	const [options, setOptions] = useState<DropdownOptions[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!category) return;
		setLoading(true);
		getDynamicDropdownByCategory(category)
			.then((res) => {
				if (res.status === 200) {
					const dropdowns: DropdownOptions[] = res.data.map((item) => ({
						value: saveLabelAsValue ? item.name : item.id,
						label: item.name,
					}));
					setOptions(dropdowns);
				}
			})
			.finally(() => setLoading(false));
	}, [category, saveLabelAsValue]);

	if (loading) return <Loading showLogo={false} fullScreen={false} />;

	const selectedValues = value.flatMap((val) => {
		const match = options.find((opt) => String(opt.value) === String(val));
		return match ? [match] : [];
	});

	return (
		<MultiDropdown
			options={options}
			selectedValues={selectedValues}
			onChange={(list) => {
				if (saveLabelAsValue) {
					onChange(list.map((item) => String(item.value)));
				} else {
					onChange(list.map((item) => Number(item.value)));
				}
			}}
			{...rest}
		/>
	);
};

export default DynamicMultiDropdownField;
